<template>
  <b-field
    v-if="type === filterType.REFBOOK"
    class="ws-field"
    :label="label"
  >
    <ws-refbook-field
      :entity="entity"
      :field="field"
      v-model="keyword"
      :guid.sync="guid"
      :item.sync="item"
      :disabled="isDisabled"
      :params="params"
      :query="query"
      :custom-formatter="customFormatter"
      :placeholder="placeholder"
      @change="onRefbookChange"
      :isFocus="true"
    >
      <template
        slot="item"
        slot-scope="props"
      >
        <slot
          name="item"
          :option="props.option"
          v-if="!!$scopedSlots.item"
        />
        <span
          v-else
          :title="getValue(props.option, true)"
        >
          {{ getValue(props.option, true) }}
        </span>
      </template>
      <template slot="empty">
        <slot name="empty"></slot>
      </template>
    </ws-refbook-field>
  </b-field>
  <b-field
    v-else-if="type === filterType.SEARCH"
    class="ws-field"
    :label="label"
  >
    <ws-search-form
      :keyword.sync="keyword"
      @update:keyword="onSearchChange"
    />
  </b-field>
  <b-field
    v-else-if="type === filterType.SELECT"
    class="ws-field"
    :label="label"
  >
    <b-select
      class="ws-field__control"
      @input="onChange"
      v-model="keyword"
      expanded
    >
      <option
        v-for="option of options"
        :key="option.value"
        :value="option.value"
        v-text="option.label"
      ></option>
    </b-select>
  </b-field>
  <b-checkbox
    v-else-if="type === filterType.CHECKBOX"
    :name="id"
    @input="onCheckboxChange"
    class="b-checkbox__centered"
    v-model="checkbox"
  >
    <b>{{label}}</b>
  </b-checkbox>
  <b-field
    v-else-if="type === filterType.DATE"
    class="ws-field"
    :label="label"
  >
    <ws-datepicker
      v-model="dateModel"
      :name="id"
      ref="datepicker"
      @input="onChangeDate"
      :position="position"
      class="ws-field__control"
    >
    </ws-datepicker>
  </b-field>
</template>

<script>
import { getValueByPath } from '@/utils/helpers'
import FilterChangeDto from '@/components/common/data/filter-change.dto'
import filterType from '@/components/common/data/filter-type.enum'
import { constants } from '@/services/constants.service'
import { mapState } from 'vuex'
import moment from 'moment'
import computedDateProp from '@/utils/computedDateProp'
export default {
  name: 'WsFilterItem',
  props: {
    x: { type: String },
    id: {
      type: String
    },
    resourceId: {
      type: String
    },
    type: {
      type: Number
    },
    entity: {
      type: String
    },
    params: {
      type: Object,
      default () {
        return {}
      }
    },
    query: {
      type: Object,
      default () {
        return {}
      }
    },
    label: {
      type: String
    },
    field: {
      type: String
    },
    defaultValue: {
      type: String,
      default () {
        return undefined
      }
    },
    options: {
      type: Array
    },
    value: {
      type: String
    },
    disabled: {
      type: Boolean
    },
    customFormatter: {
      type: Function
    },
    placeholder: {
      type: String
    },
    position: {
      type: String
    }
  },
  data () {
    return {
      guid: null,
      keyword: null,
      checkbox: null,
      item: null,
      isDisabled: this.disabled,
      date: {
        value: null
      },
      filterType,
      isDontEmit: false
    }
  },
  computed: {
    ...mapState('authentication', ['user', 'resourcesPermissions', 'currentRole']),
    dateModel: computedDateProp('date', 'value')
  },
  watch: {
    disabled (isDisabled) {
      this.isDisabled = isDisabled
    },
    async value () {
      await this.init()
    }
  },
  methods: {
    getValue (option) {
      if (!option) return
      if (typeof this.customFormatter !== 'undefined') {
        return this.customFormatter(option)
      }
      return typeof option === 'object'
        ? getValueByPath(option, this.field)
        : option
    },
    get (request) {
      if (this.params || this.query) {
        request = [request]
        if (this.params) {
          request.push(this.params)
        }
        if (this.query) {
          request.push(this.query)
        }
      }
      return this.$store.dispatch(`${this.entity}/get`, request)
    },
    getInitialValue () {
      let initialValue
      if (this.defaultValue) {
        initialValue = this.defaultValue
      }
      if (this.$route.query[this.id]) {
        initialValue = this.$route.query[this.id]
      }
      return initialValue
    },
    getResourcePermission () {
      const id = this.resourcesPermissions[this.id] ? this.id : this.resourceId
      if (this.resourcesPermissions[id] && this.resourcesPermissions[id].length > 1) { return false }
      return this.resourcesPermissions[id] &&
        this.resourcesPermissions[id].find(r => constants.ALL.indexOf(r) === -1)
    },
    isDefault (value) {
      return (!value && !this.defaultValue) || value === this.defaultValue
    },
    onRefbookChange (event) {
      this.onChange(event.guid || event[this.field])
    },
    onSearchChange (event) {
      this.onChange(event)
    },
    onCheckboxChange (event) {
      this.onChange(event)
    },
    async onChangeDate (event) {
      if (event) {
        await this.onChange(moment(this.date.value).format('YYYY-MM-DD'))
      } else {
        await this.pushState(undefined)
        this.$emit('change', new FilterChangeDto(this, undefined))
      }
    },
    async onChange (event) {
      if (!this.isDontEmit) {
        await this.pushState(event || this.defaultValue)
        this.$emit('change', new FilterChangeDto(this, event || this.defaultValue), this.isDefault(event))
      }
    },
    async handleRefbook () {
      let initialValue = this.getInitialValue()
      if (this.value) {
        await this.pushState(this.value)
        initialValue = this.value
      }
      const resourcePermission = this.getResourcePermission()
      if (initialValue || resourcePermission) {
        // this.isDisabled = true
        let guid = initialValue
        if (resourcePermission) {
          guid = resourcePermission
        }
        this.$emit('ready', new FilterChangeDto(this, guid), this.isDefault(guid))
        let item
        if (guid) {
          try {
            item = await this.get(guid)
          } catch (e) {
          }
        }
        if (item) {
          this.guid = item.guid
          this.keyword = getValueByPath(item, this.field)
          this.item = item
        }
        if (!resourcePermission) {
          this.isDisabled = false
        }
      } else {
        this.guid = null
        this.keyword = null
        this.item = null
        this.$emit('ready', new FilterChangeDto(this, this.defaultValue), true)
      }
    },
    async handleSearch () {
      let initialValue = this.getInitialValue()
      if (this.value) {
        await this.pushState(this.value)
        initialValue = this.value
      }
      if (initialValue) {
        this.keyword = initialValue
        this.$emit('ready', new FilterChangeDto(this, initialValue, this.isDefault(initialValue)))
      } else {
        this.keyword = this.defaultValue
        this.$emit('ready', new FilterChangeDto(this, this.defaultValue), true)
      }
    },
    async handleSelect () {
      let initialValue = this.getInitialValue()
      if (this.value) {
        await this.pushState(this.value)
        initialValue = this.value
      }
      if (initialValue) {
        this.keyword = initialValue
        this.$emit('ready', new FilterChangeDto(this, initialValue, this.isDefault(initialValue)))
      } else {
        this.keyword = this.defaultValue
        this.$emit('ready', new FilterChangeDto(this, this.defaultValue), true)
      }
    },
    async handleCheckbox () {
      let initialValue = this.getInitialValue()
      if (this.value) {
        await this.pushState(this.value)
        initialValue = this.value
      }
      if (initialValue) {
        this.checkbox = !!initialValue
        this.$emit('ready', new FilterChangeDto(this, initialValue, this.isDefault(initialValue)))
      } else {
        this.checkbox = !!this.defaultValue
        this.$emit('ready', new FilterChangeDto(this, this.defaultValue), true)
      }
    },
    async handleDate () {
      let initialValue = this.getInitialValue()
      if (this.value) {
        await this.pushState(this.value)
        initialValue = this.value
      }
      if (initialValue) {
        this.dateModel = initialValue
        this.$emit('ready', new FilterChangeDto(this, this.date.value, this.isDefault(initialValue)))
      } else {
        this.dateModel = this.defaultValue
        this.$emit('ready', new FilterChangeDto(this, this.defaultValue), true)
      }
    },
    async pushState (value) {
      if (value) {
        await this.$router.push({ query: { ...this.$route.query, [this.id]: value } }).catch(_ => { })
      } else {
        await this.$router.replace({ query: { ...this.$route.query, [this.id]: undefined } }).catch(_ => { })
      }
    },
    async init () {
      switch (this.type) {
        case filterType.SEARCH:
          await this.handleSearch()
          break
        case filterType.SELECT:
          await this.handleSelect()
          break
        case filterType.CHECKBOX:
          await this.handleCheckbox()
          break
        case filterType.REFBOOK:
          await this.handleRefbook()
          break
        case filterType.DATE:
          await this.handleDate()
          break
        default:
          throw new Error('Unknown filter type')
      }
    },
    async clear () {
      switch (this.type) {
        case filterType.SEARCH:
          if (this.isDefault(this.keyword)) {
            return
          }
          this.isDontEmit = true
          await this.pushState(this.defaultValue)
          await this.init()
          this.isDontEmit = false
          break
        case filterType.SELECT:
          if (this.isDefault(this.keyword)) {
            return
          }
          this.isDontEmit = true
          await this.pushState(this.defaultValue)
          await this.init()
          this.isDontEmit = false
          break
        case filterType.CHECKBOX:
          if (this.isDefault(this.checkbox)) {
            return
          }
          this.isDontEmit = true
          await this.pushState(this.defaultValue)
          await this.init()
          this.isDontEmit = false
          break
        case filterType.REFBOOK:
          if (this.isDefault(this.guid) || this.getResourcePermission()) {
            return
          }
          this.isDontEmit = true
          await this.pushState(this.defaultValue)
          await this.init()
          this.isDontEmit = false
          break
        case filterType.DATE:
          if (this.isDefault(this.dateModel)) {
            return
          }
          this.isDontEmit = true
          await this.pushState(this.defaultValue)
          await this.init()
          this.isDontEmit = false
          break
        default:
          throw new Error('Unknown filter type')
      }
    },
    setDateValue (value) {
      this.date.value = value
    }
  },
  async beforeMount () {
    await this.init()
  }
}
</script>
